import { useState } from 'react';
import { CalendarPlus, CalendarClock, Pause, Play, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { ParityForm } from './ParityForm';
import { ManagerGrid } from './ManagerGrid';
import { useManagerData, managerAPI } from './useManagerData';
import { RolloverControls } from './RolloverControls';
import { SessionCalendarForm } from './SessionCalendarForm';
import { HolidayModal } from './HolidayModal';
import { InstrumentControlModal } from './InstrumentControlModal';
import './sessions.css';

const localTime = (iso, zone) => iso ? new Date(iso).toLocaleTimeString('tr-TR', { timeZone: zone || 'UTC', hour: '2-digit', minute: '2-digit' }) : '—';

export default function SessionsPage({ permissions }) {
  const { data, error, reload } = useManagerData('/sessions/status');
  const [dialog, setDialog] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [instrumentControlOpen, setInstrumentControlOpen] = useState(false);

  const canManage = permissions.includes('manager.sessions.manage');
  const markets = Object.keys(data?.sessions || {});
  const rows = markets.map(key => {
    const value = data.sessions[key];
    return {
      id: key,
      market: key,
      status: value.status,
      reason: value.reason || '',
      timezone: value.config?.timezone,
      schedule: value.config?.description,
      source: value.config?.source,
      today_close: localTime(value.session_close, value.timezone)
    };
  });

  const control = (kind, target, halted, promptReason = true) => {
    if (!promptReason && !halted) {
      // Quick resume directly without dialog
      managerAPI.post('/sessions/control', { kind, target, halted: false, reason: 'Durdurma kaldırıldı' })
        .then(async () => {
          await reload();
          toast.success(`${target} için işlemler açıldı.`);
        })
        .catch(err => toast.error(err.message || 'Hata oluştu.'));
      return;
    }
    setDialog({
      title: halted ? 'İşlemleri Durdur' : 'Durdurmayı Kaldır',
      description: target,
      fields: [['reason', 'Açıklama', 'textarea']],
      submitLabel: halted ? 'Durdur' : 'Durdurmayı Kaldır',
      onSubmit: async values => {
        await managerAPI.post('/sessions/control', { kind, target, halted, reason: values.reason || '' });
        await reload();
        toast.success('Seans kontrolü kaydedildi.');
      }
    });
  };

  const instruments = data?.instruments || [];
  const controls = data?.controls || [];
  const haltedInstrumentsMap = new Set(
    controls.filter(c => c.kind === 'instrument' && c.halted).map(c => c.target)
  );

  const required = instruments.filter(row => row.status === 'configuration_required').length;

  const holidayRows = (data?.holidays || []).map(row => ({
    ...row,
    schedule_type: row.is_full_day === false ? `Erken Kapanış (${row.close_time || '13:00'})` : 'Tam Gün Kapalı',
    markets_label: Array.isArray(row.markets) ? (row.markets.length >= 10 ? 'Tüm Piyasalar (11)' : row.markets.join(', ')) : row.markets || '—'
  }));

  return (
    <section className="manager-data-page" data-testid="manager-sessions-page">
      <div className="manager-page-toolbar">
        <div>
          <p className="crm-eyebrow">PİYASA YÖNETİMİ</p>
          <h1>İşlem Seansları</h1>
        </div>
        <button className="sirix-btn sirix-btn-secondary" data-testid="sessions-refresh" onClick={() => reload().catch(() => {})}>
          <RefreshCw size={15} />Yenile
        </button>
      </div>

      {error && <p className="crm-error" data-testid="sessions-error">{error}</p>}
      <RolloverControls canManage={canManage} />

      <div className="parity-section-title"><h2>Piyasa Takvimleri</h2></div>
      <ManagerGrid
        name="sessions"
        rows={rows}
        fields={[["market", "Piyasa"], ["status", "Durum"], ["schedule", "Ana Seans"], ["timezone", "Saat Dilimi"], ["today_close", "Bugünkü Kapanış"], ["reason", "Açıklama"]]}
        actions={row => canManage && (
          <>
            <button className="crm-icon-button" title="Durdur" data-testid={`session-halt-${row.id}`} onClick={() => control('market', row.id, true)}>
              <Pause size={16} />
            </button>
            <button className="crm-icon-button" title="Durdurmayı kaldır" data-testid={`session-resume-${row.id}`} onClick={() => control('market', row.id, false, false)}>
              <Play size={16} />
            </button>
          </>
        )}
      />

      <div className="parity-section-title">
        <h2>Enstrüman Seansları &amp; Kontrolleri</h2>
        {canManage && (
          <button data-testid="session-instrument-control" onClick={() => setInstrumentControlOpen(true)}>
            <Pause size={15} />Enstrüman Kontrolü
          </button>
        )}
      </div>
      {required > 0 && (
        <p className="session-calendar-warning" role="status" data-testid="session-calendar-warning">
          {required} enstrüman için kurum seansı gerekli. Takvim tanımlanana kadar yeni işlemler kapalıdır.
        </p>
      )}
      <ManagerGrid
        name="instrument-calendars"
        rows={instruments}
        fields={[["symbol", "Enstrüman"], ["market_type", "Piyasa"], ["status", "Durum"], ["profile", "Takvim"], ["timezone", "Saat Dilimi"], ["reason", "Açıklama"]]}
        actions={row => canManage && (
          <>
            <button className="crm-icon-button" title="Seans takvimini düzenle" data-testid={`instrument-calendar-edit-${row.id}`} onClick={() => setCalendar(row)}>
              <CalendarClock size={16} />
            </button>
            {haltedInstrumentsMap.has(row.symbol) ? (
              <button className="crm-icon-button" title="Durdurmayı kaldır (İşlemleri aç)" data-testid={`instrument-resume-${row.symbol}`} onClick={() => control('instrument', row.symbol, false, false)}>
                <Play size={15} color="#16a34a" />
              </button>
            ) : (
              <button className="crm-icon-button" title="Enstrümanı durdur" data-testid={`instrument-halt-${row.symbol}`} onClick={() => control('instrument', row.symbol, true)}>
                <Pause size={15} color="#dc2626" />
              </button>
            )}
          </>
        )}
      />

      <div className="parity-section-title"><h2>Etkin Kurum Durdurmaları</h2></div>
      <ManagerGrid
        name="instrument-sessions"
        rows={controls.filter(row => row.kind === 'instrument' && row.halted).map(row => ({ ...row, id: row.target }))}
        fields={[["target", "Enstrüman"], ["halted", "Durduruldu"], ["reason", "Açıklama"]]}
        actions={row => canManage && (
          <button className="crm-icon-button" title="Durdurmayı kaldır" data-testid={`instrument-session-resume-${row.target}`} onClick={() => control('instrument', row.target, false, false)}>
            <Play size={15} color="#16a34a" />
          </button>
        )}
      />

      <div className="parity-section-title"><h2>Resmî Borsa Tatilleri ve Yarım Günler</h2></div>
      <ManagerGrid
        name="official-holidays"
        rows={data?.official_holidays || []}
        fields={[["date", "Tarih"], ["name", "Seans"], ["markets", "Piyasa"], ["close_time", "Sürekli İşlem Kapanışı"], ["source", "Kaynak"]]}
      />

      <div className="parity-section-title">
        <h2>Kurum Tatil Takvimi</h2>
        {canManage && (
          <button data-testid="holiday-create" onClick={() => setHolidayModalOpen(true)}>
            <CalendarPlus size={16} />Tatil Ekle
          </button>
        )}
      </div>
      <ManagerGrid
        name="holidays"
        rows={holidayRows}
        fields={[["date", "Tarih"], ["name", "Tatil Adı"], ["schedule_type", "Kapanış Türü"], ["markets_label", "Piyasalar"], ["description", "Açıklama"]]}
        actions={row => canManage && (
          <button className="crm-icon-button" title="Tatili sil" data-testid={`holiday-delete-${row.id}`} onClick={() => setDialog({
            title: 'Tatili Sil',
            description: `${row.name} (${row.date})`,
            fields: [],
            submitLabel: 'Sil',
            onSubmit: async () => {
              await managerAPI.delete(`/holidays/${row.id}`);
              await reload();
              toast.success('Tatil silindi.');
            }
          })}>
            <Trash2 size={15} />
          </button>
        )}
      />

      {dialog && <ParityForm {...dialog} testId="manager-session-form" onClose={() => setDialog(null)} />}
      {calendar && <SessionCalendarForm row={calendar} onClose={() => setCalendar(null)} onSaved={async () => { await reload(); toast.success('Seans takvimi kaydedildi.'); }} />}
      {holidayModalOpen && (
        <HolidayModal
          officialHolidays={data?.official_holidays || []}
          onClose={() => setHolidayModalOpen(false)}
          onCreated={async () => { await reload(); }}
        />
      )}
      {instrumentControlOpen && (
        <InstrumentControlModal
          instruments={instruments}
          activeControls={controls}
          onClose={() => setInstrumentControlOpen(false)}
          onUpdated={async () => { await reload(); }}
        />
      )}
    </section>
  );
}